import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";
import QuoteForm from "../components/QuoteForm";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";
import { api, resolveUrl } from "../lib/api";
import {
  Shield,
  Sparkles,
  Clock,
  HeartHandshake,
  Award,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  Car as CarIcon,
  Star,
  Users,
  Calendar,
} from "lucide-react";

const DEFAULT_HERO_IMG = "https://images.unsplash.com/photo-1671719367451-7bf05ae9549c?w=1800&q=85";
const DEFAULT_SHOWROOM_IMG = "https://images.unsplash.com/photo-1763165524637-9067debdc80b?w=1600&q=85";
const DEFAULT_STORY_IMG = "https://images.unsplash.com/photo-1597672844797-df227c7d1c20?w=1600&q=85";

export default function About() {
  const { lang } = useI18n();
  const { settings } = useSettings();
  const [stats, setStats] = useState({ reservations: 0, cars: 0, reviews: 0, rating: 0 });

  useEffect(() => {
    Promise.all([
      api.get("/cars").catch(() => ({ data: [] })),
      api.get("/reviews/stats").catch(() => ({ data: { count: 0, avg: 0 } })),
    ]).then(([cars, reviews]) => {
      setStats({
        reservations: 0, // placeholder - could be a public stat later
        cars: (cars.data || []).length,
        reviews: reviews.data?.count || 0,
        rating: reviews.data?.avg || 0,
      });
    });
  }, []);

  const contact = settings?.contact || {};
  const brand = settings?.brand_name || "Inchirieri Auto Pascani";
  const ab = settings?.about || {};

  const HERO_IMG = ab.hero_img ? resolveUrl(ab.hero_img) : DEFAULT_HERO_IMG;
  const SHOWROOM_IMG = ab.showroom_img ? resolveUrl(ab.showroom_img) : DEFAULT_SHOWROOM_IMG;
  const STORY_IMG = ab.story_img ? resolveUrl(ab.story_img) : DEFAULT_STORY_IMG;

  const DEFAULT_USPS_ICONS = [Shield, Sparkles, Clock, HeartHandshake, Award, CarIcon];
  const DEFAULT_USPS_RO = [
    { title: "Asigurare completa", desc: "RCA + CASCO incluse in fiecare rezervare. Pachete optionale cu franșiza zero." },
    { title: "Doar flota premium", desc: "Inginerie germana si rafinament italian: Audi, BMW, Porsche, Mercedes — toate din 2022 sau mai noi." },
    { title: "Flexibil si rapid", desc: "Rezervari online in mai putin de 2 minute. Livrare gratuita in Pascani. Check-in express." },
    { title: "Pret transparent", desc: "Toate taxele, kilometrii si asigurarea inclusi. Fara taxe ascunse, garantat." },
    { title: "Local si de incredere", desc: "Afacere de familie cu sediul in Pascani, servim regiunea Iasi pentru mobilitate premium." },
    { title: "Stare impecabila", desc: "Fiecare masina este detaliata inainte de fiecare rezervare. Calitate de showroom interior si exterior." },
  ];
  const DEFAULT_USPS_EN = [
    { title: "Comprehensive insurance", desc: "Full RCA + CASCO coverage included in every booking, with optional zero-deductible packages." },
    { title: "Premium-only fleet", desc: "Hand-picked German engineering and Italian flair: Audi, BMW, Porsche, Mercedes — all 2022 or newer." },
    { title: "Flexible & fast", desc: "Online booking in under 2 minutes. Free delivery within Pascani. Express check-in." },
    { title: "Honest pricing", desc: "All taxes, mileage and insurance included. No hidden fees, ever." },
    { title: "Local & trusted", desc: "Family business based in Pascani, serving the Iasi region for premium mobility needs." },
    { title: "Pristine condition", desc: "Every vehicle is detailed before each booking. Showroom-quality interior and exterior." },
  ];

  const rawUsps = ab.usps || [];
  const usps = DEFAULT_USPS_ICONS.map((Icon, i) => ({
    icon: Icon,
    title: lang === "en"
      ? (rawUsps[i]?.title_en || DEFAULT_USPS_EN[i].title)
      : (rawUsps[i]?.title_ro || DEFAULT_USPS_RO[i].title),
    desc: lang === "en"
      ? (rawUsps[i]?.desc_en || DEFAULT_USPS_EN[i].desc)
      : (rawUsps[i]?.desc_ro || DEFAULT_USPS_RO[i].desc),
  }));

  const story = {
    eyebrow: lang === "en" ? (ab.story_eyebrow_en || "Our story") : (ab.story_eyebrow_ro || "Povestea noastra"),
    title: lang === "en" ? (ab.story_title_en || "Born from a passion for fine automobiles") : (ab.story_title_ro || "Nascut din pasiunea pentru masini fine"),
    p1: lang === "en"
      ? (ab.story_p1_en || "Inchirieri Auto Pascani started in 2018 as a small family operation with three vehicles and an obsession for detail. Today, we operate one of the most exclusive rental fleets in the Iasi region.")
      : (ab.story_p1_ro || "Inchirieri Auto Pascani a inceput in 2018 ca o mica afacere de familie cu trei masini si o obsesie pentru detaliu. Astazi, operam una dintre cele mai exclusiviste flote de inchiriere din regiunea Iasi."),
    p2: lang === "en"
      ? (ab.story_p2_en || "Our mission is simple: deliver an effortless experience — from the first phone call to the keys being handed back. We believe luxury isn't just the car, it's how it makes you feel.")
      : (ab.story_p2_ro || "Misiunea noastra e simpla: o experienta fara batai de cap — de la primul telefon, pana cand cheile sunt inapoiate. Credem ca luxul nu e doar masina, ci felul in care te face sa te simti."),
    p3: lang === "en"
      ? (ab.story_p3_en || "Every vehicle in our fleet is personally maintained, cleaned and inspected by our team. We don't rent cars — we lend you our pride and joy.")
      : (ab.story_p3_ro || "Fiecare masina din flota noastra este intretinuta, curatata si inspectata personal de echipa noastra. Nu inchiriem masini — iti dam mandria si bucuria noastra in grija."),
  };

  const quote = lang === "en"
    ? (ab.quote_en || "We don't sell rentals — we craft experiences.")
    : (ab.quote_ro || "Nu vindem inchirieri — cream experiente.");

  const cta = {
    eyebrow: lang === "en" ? (ab.cta_eyebrow_en || "Ready when you are") : (ab.cta_eyebrow_ro || "Gata cand esti tu"),
    title: lang === "en" ? (ab.cta_title_en || "Let's plan your next drive") : (ab.cta_title_ro || "Hai sa-ti planificam urmatoarea calatorie"),
    text: lang === "en"
      ? (ab.cta_text_en || "Browse our collection or request a personalised quote — we'll get back to you within 30 minutes.")
      : (ab.cta_text_ro || "Exploreaza colectia sau cere o oferta personalizata — revenim in maxim 30 minute."),
    primary: lang === "en" ? "Explore the fleet" : "Vezi flota",
    secondary: lang === "en" ? "Request a quote" : "Cere oferta",
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SeoHead
        title={lang === "en" ? `About us — ${brand}` : `Despre noi — ${brand}`}
        description={lang === "en" ? "A family-run premium car rental in Pascani, Romania. Hand-picked fleet, all-inclusive pricing, effortless experience." : "Inchirieri auto premium in Pascani, afacere de familie. Flota selectionata, pret transparent, experienta fara griji."}
      />
      <PublicHeader />

      {/* HERO */}
      <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.9) 100%)" }} />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <div className="label-eyebrow mb-6" style={{ color: "var(--brand-primary)" }}>
            {lang === "en" ? "About us" : "Despre noi"}
          </div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl md:text-7xl tracking-tight leading-tight mb-6">
            {lang === "en"
              ? (ab.hero_title_en || "More than a rental — a partnership")
              : (ab.hero_title_ro || "Mai mult decat o inchiriere — o relatie")}
          </h1>
          <p className="text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--brand-text-secondary)" }}>
            {lang === "en"
              ? (ab.hero_subtitle_en || "Premium cars, family service. Discover what makes us the most trusted car rental in Pascani.")
              : (ab.hero_subtitle_ro || "Masini premium, servicii de familie. Descopera ce ne face cea mai de incredere inchiriere auto din Pascani.")}
          </p>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y" style={{ borderColor: "var(--brand-border)" }} data-testid="about-stats">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="font-serif text-4xl mb-1" style={{ color: "var(--brand-primary)" }}>
              {stats.cars}+
            </div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--brand-text-secondary)" }}>
              {lang === "en" ? "Premium cars" : "Masini premium"}
            </div>
          </div>
          <div>
            <div className="font-serif text-4xl mb-1" style={{ color: "var(--brand-primary)" }}>
              500+
            </div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--brand-text-secondary)" }}>
              {lang === "en" ? "Happy clients" : "Clienti multumiti"}
            </div>
          </div>
          <div>
            <div className="font-serif text-4xl mb-1 inline-flex items-baseline gap-1" style={{ color: "var(--brand-primary)" }}>
              {stats.rating > 0 ? stats.rating.toFixed(1) : "5.0"}
              <Star className="w-5 h-5" fill="currentColor" />
            </div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--brand-text-secondary)" }}>
              {lang === "en" ? `${stats.reviews}+ reviews` : `${stats.reviews}+ recenzii`}
            </div>
          </div>
          <div>
            <div className="font-serif text-4xl mb-1" style={{ color: "var(--brand-primary)" }}>
              7
            </div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--brand-text-secondary)" }}>
              {lang === "en" ? "Years experience" : "Ani de experienta"}
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 md:py-32 px-6 md:px-12 max-w-[1200px] mx-auto" data-testid="about-story">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <img src={STORY_IMG} alt="" className="w-full aspect-[4/3] object-cover" />
            <div className="absolute -bottom-6 -right-6 px-6 py-4 hidden md:block" style={{ background: "var(--brand-primary)", color: "var(--brand-bg)" }}>
              <div className="font-serif text-2xl">Est. 2018</div>
              <div className="text-xs uppercase tracking-widest">Pascani, Iasi</div>
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-4" style={{ color: "var(--brand-primary)" }}>{story.eyebrow}</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mb-8 leading-tight">{story.title}</h2>
            <p className="text-base leading-relaxed mb-5" style={{ color: "var(--brand-text-secondary)" }}>{story.p1}</p>
            <p className="text-base leading-relaxed mb-5" style={{ color: "var(--brand-text-secondary)" }}>{story.p2}</p>
            <p className="text-base leading-relaxed" style={{ color: "var(--brand-text-secondary)" }}>{story.p3}</p>
          </div>
        </div>
      </section>

      {/* USPS */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[rgba(255,255,255,0.02)]" data-testid="about-usps">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <div className="label-eyebrow mb-4" style={{ color: "var(--brand-primary)" }}>
              {lang === "en" ? "Why choose us" : "De ce noi"}
            </div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">
              {lang === "en" ? "Six promises we never break" : "Sase promisiuni pe care nu le incalcam niciodata"}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {usps.map((u, i) => (
              <div key={i} className="card-surface p-7" data-testid={`usp-${i}`}>
                <div className="w-12 h-12 flex items-center justify-center mb-4 rounded-full" style={{ background: "rgba(201,169,97,0.12)", color: "var(--brand-primary)" }}>
                  <u.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-xl mb-2">{u.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--brand-text-secondary)" }}>{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWROOM / VISUAL BREAK */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden" data-testid="about-showroom">
        <img src={SHOWROOM_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.7) 100%)" }}>
          <div className="text-center px-6 max-w-2xl">
            <p className="font-serif font-light text-3xl sm:text-4xl italic leading-tight" style={{ color: "var(--brand-primary)" }}>
              "{quote}"
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT TILE */}
      <section className="py-24 md:py-32 px-6 md:px-12 max-w-[1200px] mx-auto" data-testid="about-contact">
        <div className="card-surface p-10 md:p-14 text-center">
          <div className="label-eyebrow mb-4" style={{ color: "var(--brand-primary)" }}>
            {cta.eyebrow}
          </div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mb-6">{cta.title}</h2>
          <p className="text-base leading-relaxed max-w-xl mx-auto mb-10" style={{ color: "var(--brand-text-secondary)" }}>
            {cta.text}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-10">
            <Link to="/#fleet" className="btn-gold-filled inline-flex items-center gap-2" data-testid="about-cta-primary">
              {cta.primary} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/#quote" className="btn-ghost inline-flex items-center gap-2" data-testid="about-cta-secondary">
              {cta.secondary}
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 text-sm border-t pt-8" style={{ borderColor: "var(--brand-border)", color: "var(--brand-text-secondary)" }}>
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="inline-flex items-center justify-center gap-2 hover:text-[var(--brand-primary)]" data-testid="about-phone">
                <Phone className="w-4 h-4" /> {contact.phone}
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="inline-flex items-center justify-center gap-2 hover:text-[var(--brand-primary)]" data-testid="about-email">
                <Mail className="w-4 h-4" /> {contact.email}
              </a>
            )}
            {contact.address && (
              <span className="inline-flex items-center justify-center gap-2" data-testid="about-address">
                <MapPin className="w-4 h-4" /> {contact.address}
              </span>
            )}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
